import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSearch } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, ControlsContainer } from '../../common/styles';
import {
  MainContainer,
  CertsGrid,
  CertCard,
  CertCardContent,
  CertTitle,
  EmployeeBadge,
  CountBadge,
  ActionButtons
} from './styles';
import { COLORS } from '../../../constants/radar';
import { useAlert } from '../../../contexts/AlertContext';
import { cvManagementApi } from '../../../services/api';
import { getRelativeTime } from '../../../utils/dateUtils';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ConsultantAttachmentFilter from '../../common/filters/ConsultantAttachmentFilter';
import CertificationIssuerFilter from '../../common/filters/CertificationIssuerFilter';
import { ISSUER_TYPE_FILTER } from '../../../constants/certificationFilters';

const CertificationNameReconciliation = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [customCerts, setCustomCerts] = useState([]);
  const [catalogEntries, setCatalogEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCert, setCurrentCert] = useState({ name: '', count: 0, issuer: null });
  const [selectedCatalogEntry, setSelectedCatalogEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('attached');
  const [selectedIssuerId, setSelectedIssuerId] = useState('');
  const [issuerTypeFilter, setIssuerTypeFilter] = useState(ISSUER_TYPE_FILTER.ALL);
  const [showControls, setShowControls] = useState(true);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  useEffect(() => {
    fetchAllCustomCertifications();
  }, []);

  const fetchAllCustomCertifications = async () => {
    try {
      setLoading(true);
      const response = await cvManagementApi.getAllCustomCertifications();
      setCustomCerts(response?.customCertifications || []);
    } catch (error) {
      console.error('Error fetching custom certifications:', error);
      showAlert('Failed to load custom certifications', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalog = async (issuerId) => {
    try {
      setLoadingCatalog(true);
      const data = await cvManagementApi.getCatalogByIssuer(issuerId);
      setCatalogEntries(data || []);
    } catch (error) {
      console.error('Error fetching catalog:', error);
    } finally {
      setLoadingCatalog(false);
    }
  };

  const handleOpenDialog = (cert) => {
    setCurrentCert(cert);
    setSelectedCatalogEntry(null);
    setOpenDialog(true);

    if (cert.issuer?.id) {
      fetchCatalog(cert.issuer.id);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCatalogEntry(null);
  };

  const handleReconcile = async () => {
    if (!selectedCatalogEntry?.id) {
      showAlert('Please select a catalog certification to reconcile with', 'error');
      return;
    }

    if (!currentCert.issuer?.id) {
      showAlert('No issuer associated with this certification', 'error');
      return;
    }

    try {
      await cvManagementApi.reconcileCertification(
        currentCert.issuer.id,
        currentCert.name,
        selectedCatalogEntry.id
      );

      showAlert('Certification reconciled successfully', 'success');
      await fetchAllCustomCertifications();
      handleCloseDialog();
    } catch (error) {
      console.error('Error reconciling certification:', error);
      showAlert(`Failed to reconcile certification: ${error.message}`, 'error');
    }
  };

  const filteredCerts = customCerts.filter(cert => {
    const isUnattached = cert.employees.length === 1 && cert.employees[0].employeeId === null;

    if (employeeFilter === 'attached' && isUnattached) {
      return false;
    }
    if (employeeFilter === 'unattached' && !isUnattached) {
      return false;
    }

    if (selectedIssuerId && cert.issuer?.id !== selectedIssuerId) {
      return false;
    }

    if (issuerTypeFilter === ISSUER_TYPE_FILTER.CUSTOM && !cert.issuer?.isCustom) {
      return false;
    }
    if (issuerTypeFilter === ISSUER_TYPE_FILTER.STANDARD && cert.issuer?.isCustom) {
      return false;
    }

    const term = (searchTerm || '').toLowerCase();
    return cert.name.toLowerCase().includes(term) ||
      (cert.employees && cert.employees.some(emp => emp.fullName.toLowerCase().includes(term))) ||
      (cert.issuer && cert.issuer.name.toLowerCase().includes(term));
  });

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/cv-configuration')}>
            <FaArrowLeft /> Back to CV Configuration
          </BackButton>
          <Title>Certification Name Reconciliation</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start'
          }}>
            <TextField
              placeholder="Search custom certifications"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch style={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: '500px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '40px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 14px',
                }
              }}
            />
            <ConsultantAttachmentFilter
              value={employeeFilter}
              onChange={setEmployeeFilter}
            />
            <FormControl
              size="small"
              sx={{
                minWidth: '180px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '40px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  }
                }
              }}
            >
              <InputLabel sx={{ fontSize: '14px' }}>Issuer Type</InputLabel>
              <Select
                value={issuerTypeFilter}
                label="Issuer Type"
                onChange={(e) => {
                  const newValue = e.target.value;
                  setIssuerTypeFilter(newValue);
                  if (newValue === ISSUER_TYPE_FILTER.CUSTOM) {
                    setSelectedIssuerId('');
                  }
                }}
              >
                <MenuItem value={ISSUER_TYPE_FILTER.ALL}>All</MenuItem>
                <MenuItem value={ISSUER_TYPE_FILTER.CUSTOM}>Custom issuers</MenuItem>
                <MenuItem value={ISSUER_TYPE_FILTER.STANDARD}>Standard issuers</MenuItem>
              </Select>
            </FormControl>
            <CertificationIssuerFilter
              value={selectedIssuerId}
              onChange={setSelectedIssuerId}
              disabled={issuerTypeFilter === ISSUER_TYPE_FILTER.CUSTOM}
            />
          </Box>
        </ControlsContainer>

        <MainContainer>
          {!loading && (
            <div style={{
              color: '#64748B',
              fontSize: '14px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{ fontWeight: 600 }}>
                {filteredCerts.length}
              </span>
              custom {filteredCerts.length === 1 ? 'certification' : 'certifications'} found
            </div>
          )}

          {loading ? (
            <LoadingSpinner message="Loading custom certifications..." />
          ) : filteredCerts.length === 0 ? (
            <EmptyState
              title="No custom certifications found"
              description={searchTerm || employeeFilter !== 'all' || selectedIssuerId || issuerTypeFilter !== ISSUER_TYPE_FILTER.ALL
                ? "No custom certifications match your current filters. Try adjusting your search or filters."
                : "All certifications are already mapped to catalog entries."}
            />
          ) : (
            <CertsGrid>
              {filteredCerts.map((cert, index) => (
                <CertCard key={`${cert.name}-${index}`}>
                  <CertCardContent>
                    <CertTitle>
                      {cert.name}
                      <CountBadge>
                        {cert.count} {cert.count === 1 ? 'usage' : 'usages'}
                      </CountBadge>
                    </CertTitle>

                    {cert.employees && cert.employees.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {cert.employees.map((emp) => (
                          <EmployeeBadge key={emp.id}>
                            <PersonIcon fontSize="small" /> {emp.fullName}
                          </EmployeeBadge>
                        ))}
                      </Box>
                    )}

                    {cert.issuer && (
                      <EmployeeBadge>
                        <BusinessIcon fontSize="small" /> {cert.issuer.name}
                        {cert.issuer.isCustom && (
                          <span style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            color: '#92400e',
                            backgroundColor: '#fde68a',
                            borderRadius: '4px',
                            padding: '1px 6px',
                            marginLeft: '4px'
                          }}>
                            custom
                          </span>
                        )}
                      </EmployeeBadge>
                    )}

                    {cert.createdAt && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#94a3b8',
                        fontSize: '12px',
                        marginTop: '8px'
                      }}>
                        <AccessTimeIcon sx={{ fontSize: 14 }} /> Added {getRelativeTime(cert.createdAt)}
                      </div>
                    )}

                    <ActionButtons>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(cert)}
                        title="Reconcile with catalog certification"
                      >
                        <LinkIcon />
                      </IconButton>
                    </ActionButtons>
                  </CertCardContent>
                </CertCard>
              ))}
            </CertsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Reconcile Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: { xs: '95%', sm: '80%', md: '800px' },
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle>Reconcile Custom Certification</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Reconcile the custom certification <strong>"{currentCert.name}"</strong> ({currentCert.count} {currentCert.count === 1 ? 'usage' : 'usages'}) with a catalog certification
            {currentCert.issuer && <> from <strong>{currentCert.issuer.name}</strong></>}.
          </DialogContentText>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 500 }}>
              Select a Catalog Certification
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              {loadingCatalog ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading catalog certifications...
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  options={catalogEntries}
                  getOptionLabel={(option) => option.name || ''}
                  value={selectedCatalogEntry}
                  onChange={(event, newValue) => setSelectedCatalogEntry(newValue || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Catalog Certification"
                      placeholder="Search for a certification..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        {option.category && (
                          <Typography variant="caption" color="text.secondary">
                            Category: {option.category.name}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  noOptionsText="No catalog certifications found. Add certifications to this issuer's catalog first."
                  fullWidth
                />
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleReconcile}
            variant="contained"
            startIcon={<LinkIcon />}
            disabled={!selectedCatalogEntry?.id}
            sx={{
              bgcolor: COLORS.PRIMARY,
              '&:hover': {
                bgcolor: COLORS.PRIMARY_DARK,
              }
            }}
          >
            Reconcile
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CertificationNameReconciliation;
